import type { Animal, DrawStep } from "../data/animals";
import { nameStep, spellOut, toGlyphs } from "../data/handwriting";
import type { Locale } from "./locale";
import type { Gender, Strings } from "./strings";
import {
  ALIASES_FR,
  FACTS_FR,
  HINTS_FR,
  NAMES_FR,
  TITLES_FR,
  nameHintFr,
  type NameFr,
} from "./drawings.fr";
import {
  ALIASES_EL,
  FACTS_EL,
  HINTS_EL,
  NAMES_EL,
  TITLES_EL,
  nameHintEl,
} from "./drawings.el";
import {
  ALIASES_NL,
  FACTS_NL,
  HINTS_NL,
  NAMES_NL,
  TITLES_NL,
  nameHintNl,
} from "./drawings.nl";
import { ALIASES_EN } from "./drawings.en";
import { CUPS_FR, REWARDS_FR, type RewardFr } from "./rewards.fr";
import { CUPS_EL, REWARDS_EL } from "./rewards.el";
import { CUPS_NL, REWARDS_NL } from "./rewards.nl";

export interface Tables {
  names: Record<string, NameFr>;
  hints: Record<string, string>;
  titles: Record<string, string>;
  facts: Record<string, string>;
  /** Builds the "write the name" hint from the spelt-out letters. */
  nameHint: (spelt: string) => string;
}

/**
 * English needs no overlay — its data files already are the source. Exported so
 * `scripts/check-translation-coverage.mjs` can enforce that every drawing in
 * every language actually has text, the way the narration gate enforces audio.
 */
export const TABLES: Partial<Record<Locale, Tables>> = {
  fr: {
    names: NAMES_FR,
    hints: HINTS_FR,
    titles: TITLES_FR,
    facts: FACTS_FR,
    nameHint: nameHintFr,
  },
  el: {
    names: NAMES_EL,
    hints: HINTS_EL,
    titles: TITLES_EL,
    facts: FACTS_EL,
    nameHint: nameHintEl,
  },
  nl: {
    names: NAMES_NL,
    hints: HINTS_NL,
    titles: TITLES_NL,
    facts: FACTS_NL,
    nameHint: nameHintNl,
  },
};

const ALIASES: Record<Locale, Record<string, string[]>> = {
  en: ALIASES_EN,
  fr: ALIASES_FR,
  el: ALIASES_EL,
  nl: ALIASES_NL,
};

/**
 * Redraw the "write the name" finale in `locale`. The step remembers the word
 * and the layout it was built from (see `nameStep`), so the translated word is
 * laid out identically — same baseline, same cap height, auto-shrunk to fit,
 * which is what keeps a word like PARALLÉLOGRAMME inside the canvas.
 */
function localizeNameStep(step: DrawStep, name: NameFr, t: Tables): DrawStep {
  const word = name.written ?? name.name;
  return nameStep(word, {
    ...step.nameOpts,
    hint: t.nameHint(spellOut(toGlyphs(word))),
  });
}

/**
 * Does this "write the name" step spell the DRAWING's own name?
 *
 * Usually yes — but not always. The Rain Cloud writes just "CLOUD", and a step
 * could spell something else entirely (a footballer's name on a jersey). Only
 * a step that writes the drawing's own name may be swapped for the translated
 * name; anything else keeps its letters, and only its hint is translated. An
 * explicit `written` form says "yes, redraw it, but with this word".
 */
function spellsOwnName(step: DrawStep, english: Animal, name: NameFr): boolean {
  return (
    name.written !== undefined ||
    toGlyphs(step.nameWord ?? "") === toGlyphs(english.name)
  );
}

/**
 * A copy of `animal` with its name, title, fact and step hints in `locale`.
 * Untranslated drawings (and every drawing in English) are returned unchanged,
 * so the object identity is stable and React doesn't re-render for nothing.
 */
export function localizeAnimal(animal: Animal, locale: Locale): Animal {
  const t = TABLES[locale];
  if (!t) return animal;

  const name = t.names[animal.id];
  let changed = false;

  const steps = animal.steps.map((step) => {
    if (step.nameWord !== undefined && name && spellsOwnName(step, animal, name)) {
      // Without a translated name (or when the step spells something else
      // entirely) leave the English lettering alone: a French caption over
      // English letters would be worse than not translating at all.
      changed = true;
      return localizeNameStep(step, name, t);
    }

    const hint = t.hints[step.hint];
    const fills = step.fills?.map((f) =>
      f.hint && t.hints[f.hint] ? { ...f, hint: t.hints[f.hint] } : f,
    );
    const fillsChanged = fills?.some((f, i) => f !== step.fills?.[i]) ?? false;
    if (!hint && !fillsChanged) return step;
    changed = true;
    return { ...step, ...(hint ? { hint } : {}), ...(fills ? { fills } : {}) };
  });

  const title = animal.title ? t.titles[animal.title] : undefined;
  const fact = animal.fact ? t.facts[animal.fact] : undefined;
  if (!name && !title && !fact && !changed) return animal;

  return {
    ...animal,
    ...(name ? { name: name.name } : {}),
    ...(title ? { title } : {}),
    ...(fact ? { fact } : {}),
    steps,
  };
}

/** Grammatical gender of a drawing's NAME in `locale` (masculine by default).
 *  Use for sentences about the drawing itself: "Tu as dessiné une France !" */
export function genderOf(animalId: string, locale: Locale): Gender {
  return TABLES[locale]?.names[animalId]?.gender ?? "m";
}

/**
 * Gender of the subject as the heading names it — the `title` when there is one,
 * otherwise the name. A flag's heading reads "Comment dessiner un drapeau
 * français": masculine, because `drapeau` is, even though *la* France is not.
 */
export function subjectGenderOf(animalId: string, locale: Locale): Gender {
  const n = TABLES[locale]?.names[animalId];
  return n?.titleGender ?? n?.gender ?? "m";
}

/**
 * The phrase to slot after "How to draw …" in the heading. English and French
 * decline nothing, so they return the nominative subject unchanged (the `title`
 * if there is one, else the name) — identical to the old inline expression.
 *
 * Greek's verb ζωγραφίζω takes the ACCUSATIVE, and the article isn't derivable
 * from gender alone (masculine "έναν" vs "ένα" depends on the next sound), so
 * the whole phrase is stored: "έναν σκύλο", "μια γάτα", "ένα δέντρο". Flags read
 * "μια <ελληνική σημαία>" — σημαία is feminine and its accusative equals its
 * nominative, so the localized title needs only the feminine article prepended.
 * `animal` is expected to have been through `localizeAnimal`.
 */
export function accusativeOf(animal: Animal, locale: Locale): string {
  if (locale !== "el") return animal.title ?? animal.name;
  if (animal.title) return `μια ${animal.title}`;
  return TABLES.el?.names[animal.id]?.accusative ?? animal.name;
}

/**
 * Spoken words that select this drawing: the aliases for this locale, else the
 * (already localized) name. `animal` is expected to have been through
 * `localizeAnimal`, so the fallback is in the right language.
 */
export function aliasesFor(animal: Animal, locale: Locale): string[] {
  return ALIASES[locale][animal.id] ?? [animal.name.toLowerCase()];
}

/**
 * `LEVEL_LABEL` in animals.ts is English ("Easy" / "Medium" / "Harder") and is
 * baked into the level-up payload. Map it back to the current language rather
 * than threading a locale through the data layer.
 */
export function localizeLevelLabel(englishLabel: string, t: Strings): string {
  switch (englishLabel) {
    case "Easy":
      return t.levelEasy;
    case "Medium":
      return t.levelMedium;
    case "Harder":
      return t.levelHarder;
    default:
      return englishLabel;
  }
}

/**
 * A reward or cup as the child should read it: its French name, and the
 * definite article the sentence needs ("le Renard", "la Souris", "l'Éléphant").
 * The English name stays the key — `FRIEND_DRAWINGS` is looked up by it.
 */
export function rewardTextOf(englishName: string, locale: Locale): RewardFr {
  const table = locale === "fr" ? REWARDS_FR : locale === "el" ? REWARDS_EL : locale === "nl" ? REWARDS_NL : undefined;
  return table?.[englishName] ?? { name: englishName, article: "" };
}

/** Same, for the football cups. */
export function cupTextOf(englishName: string, locale: Locale): RewardFr {
  const table = locale === "fr" ? CUPS_FR : locale === "el" ? CUPS_EL : locale === "nl" ? CUPS_NL : undefined;
  return table?.[englishName] ?? { name: englishName, article: "" };
}
